import { v4 as uuidv4 } from 'uuid';

import {
  ErrorTypes,
  EventTypes,
  AgentChatEvents,
  UserChatEvents,
  LiveChatClient,
  ChatEndedReason,
  ChatEndedReasonTypes,
} from '@leagueplatform/live-chat';

import { captureAppMessage } from '@leagueplatform/observability';
import {
  DEFAULT_PARTNER_ID,
  BENEFIT_BOOKLET_DOWNLOAD_PREFIX,
  BENEFIT_BOOKLET_DOWNLOAD_URL_MARKER,
  BENEFIT_BOOKLET_DOCUMENT_KEY_PARAM,
  SENTRY_CONSTANTS,
} from 'common/constants';
import { IdentityToken } from 'utils/class-identity-token';
import { hasPhoneNumber } from 'utils/is-phone-number.util';
import { linkifyPhoneNumbers } from 'utils/linkify-phone-numbers.util';

import {
  EnGenChat,
  ChatConfig,
  Environments,
  ChatError,
  ChatParticipant,
  StatusChange,
  ChatState,
  ConnectionState,
  ParticipantMessage,
  MessageType,
  ButtonContent,
  ParticipantUpdate,
  Parameter,
  CardCarouselContent,
  CardContent,
} from '@highmark-web/engen-chat-sdk';

type UnacknowledgedMessage = {
  messageId: string;
  eventId: string;
};

type Config = {
  [key: string]: any;
};

export class ChatInstance {
  status: ChatState;

  connectionStatus: ConnectionState;

  config: Config;

  unacknowledgedMessages: UnacknowledgedMessage[];

  constructor() {
    this.status = ChatState.READY;
    this.connectionStatus = ConnectionState.CONNECTING;
    this.unacknowledgedMessages = [];
    this.config = {};
  }

  setStatus(newStatus: ChatState, connectStatus: ConnectionState) {
    if (
      newStatus === ChatState.READY &&
      connectStatus === ConnectionState.CONNECTING
    ) {
      return;
    }
    this.status = newStatus;
  }

  getStatus() {
    return this.status;
  }

  addUnacknowledgedMessage(messageId: string, eventId: string) {
    this.unacknowledgedMessages.push({ messageId, eventId });
  }

  acknowledgeMessage(messageId: string) {
    // Find the index for the acknowledgement message.
    const index = this.unacknowledgedMessages.findIndex(
      (message: UnacknowledgedMessage) => message.messageId === messageId,
    );
    // If it exists inside of the unacknowledged messages
    if (index > -1) {
      // Splice it out of the array
      const acknowledgeMessage = this.unacknowledgedMessages.splice(index, 1);
      // Return the eventId
      return acknowledgeMessage[0].eventId;
    }
    return null;
  }

  setConfig(newConfig: Config) {
    this.config = newConfig;
  }

  getConfigValue(key: string) {
    return this.config[key] || null;
  }

  getLinkValue = (link: ButtonContent) => {
    const rawLinkValue = link?.action?.link?.value;

    // Handle benefit booklet download action
    if (rawLinkValue?.startsWith(BENEFIT_BOOKLET_DOWNLOAD_PREFIX)) {
      // get document key from the link value
      const documentKey = rawLinkValue.substring(
        BENEFIT_BOOKLET_DOWNLOAD_PREFIX.length,
      );
      return `${BENEFIT_BOOKLET_DOWNLOAD_URL_MARKER}?${BENEFIT_BOOKLET_DOCUMENT_KEY_PARAM}=${encodeURIComponent(documentKey)}`;
    }

    if (
      link?.action?.link?.linkType === 'INTERNAL' &&
      link?.action?.link?.source === 'CONFIG' &&
      rawLinkValue
    ) {
      const linkValue = this.getConfigValue(rawLinkValue);
      if (linkValue) {
        return linkValue;
      }
      const defaultLink = this.getConfigValue('DEFAULT_DEEPLINK');
      if (defaultLink) {
        return `${window.location.origin}${defaultLink}`;
      }
      return `${window.location.origin}/`;
    }
    return rawLinkValue ?? null;
  };

  getMessageLink = (link: ButtonContent) => {
    const linkValue = this.getLinkValue(link);
    return {
      value: linkValue,
      label: link.text ?? null,
    };
  };

  // Gets links for messages with a type of Card
  getMessageLinks = (links: ButtonContent[]) =>
    links.map((link: ButtonContent) => this.getMessageLink(link));
}

export type EnGenLiveChatSettingsEnv = 'staging' | 'production';
export interface EnGenLiveChatSettings {
  env: EnGenLiveChatSettingsEnv;
  partnerId?: string;
  onChatEnd?: () => void;
}

const isStage = (env: EnGenLiveChatSettingsEnv) => env === 'staging';

export const createLiveChatClient = ({
  env,
  partnerId,
  onChatEnd,
}: EnGenLiveChatSettings): LiveChatClient => {
  const chatEnvironment = isStage(env) ? Environments.UAT : Environments.PROD;
  const chatConfig: ChatConfig = new ChatConfig(chatEnvironment);
  const chatSDK = new EnGenChat(chatConfig);

  const identityToken = new IdentityToken(partnerId ?? DEFAULT_PARTNER_ID);
  const chatInstance = new ChatInstance();

  // Set once `subscribeToAgentChatEvents` runs so that terminal states reached
  // outside that subscription (a rejected `closeChat`) can still notify the UI.
  let agentEventHandler: ((event: AgentChatEvents) => void) | null = null;

  // A single chat termination can be reported more than once: an `error` frame
  // carrying `endChat` emits both an error and a COMPLETE status change, the
  // SDK re-emits COMPLETE as the socket unwinds, and repeated config resolution
  // can leave more than one live subscription on the SDK. The UI event and the
  // survey must each happen exactly once per session.
  let chatEndHandled = false;

  const endChat = (reason: ChatEndedReason) => {
    if (chatEndHandled) {
      return;
    }
    chatEndHandled = true;
    agentEventHandler?.({
      type: EventTypes.CHAT_ENDED,
      eventId: uuidv4(),
      reason,
    });
    onChatEnd?.();
  };

  // Gets sender for messages
  const getMessageSender = (participant: ChatParticipant) => ({
    id: `${participant?.id ?? uuidv4()}`,
    name: participant?.displayName,
    avatarUrl: participant?.icon?.url?.value,
  });

  // Gets suggestion chips for messages
  const getMessageChips = (suggestions: ButtonContent[]) =>
    suggestions.map((suggestion: ButtonContent) => ({
      label: suggestion.text,
      value: suggestion?.action?.event?.name || suggestion.text,
      link: suggestion?.action?.link && chatInstance.getMessageLink(suggestion),
      metadata: suggestion?.action?.event?.parameters ?? [],
    }));

  // Gets card content for messages with a type of Card
  const getMessageCard = (card: any) => {
    const buttons =
      card.buttons && card.buttons.length > 0
        ? chatInstance.getMessageLinks(card.buttons)
        : [];

    return {
      title: card.title,
      subtitle: card?.subtitle,
      bodyText: card?.bodyText,
      buttons,
    };
  };

  // Gets card content for messages with a type of Carousel
  const getMessageCardList = (carousel: CardCarouselContent) =>
    carousel.cards.map((card: CardContent) => getMessageCard(card));

  const checkAvailability = async () => {
    const freshIdentityToken = await identityToken.getToken();
    if (freshIdentityToken) {
      try {
        const providers = await chatSDK.getProviders(freshIdentityToken);
        const allProviders = await chatSDK.getAvailability(
          freshIdentityToken,
          providers[0]?.providerId,
        );
        if (allProviders) {
          return allProviders?.provider.providerId;
        }
        return null;
      } catch (err) {
        return null;
      }
    }
    return null;
  };

  // Starts chat
  const startChat = async (providerId: string) => {
    const freshIdentityToken = await identityToken.getToken();
    if (freshIdentityToken) {
      chatSDK.startChat(freshIdentityToken, providerId, []);
    }
  };

  const sendMessage = async (message: string) => {
    const freshIdentityToken = await identityToken.getToken();
    if (freshIdentityToken) {
      const messageStatus = chatSDK.sendMessage(freshIdentityToken, message);
      return messageStatus;
    }
    return null;
  };

  const sendChip = async (chip: {
    label: string;
    value: string;
    metadata: { [key: string]: any };
  }) => {
    const freshIdentityToken = await identityToken.getToken();
    if (freshIdentityToken) {
      let parameters: Parameter[] = [];

      // If chip.metadata is an object of key/values, transform to expected Parameters[] format
      // This is to match live-chat SDK's Chip metadata format to enGen's Event parameters
      // If chip.metadata is already an array, assume it's matching the same format already
      if (Array.isArray(chip.metadata)) {
        parameters = chip.metadata;
      } else if (chip.metadata) {
        parameters = Object.entries(chip.metadata).map(([key, value]) => ({
          name: key,
          value,
        }));
      }

      const content: ButtonContent = {
        text: chip.label,
        action: {
          // actionType: 'EVENT', //  Ensure this is of type ActionType (EVENT, LINK, SSO)
          event: { name: chip.value, parameters },
        },
      };

      try {
        const response = chatSDK.sendEvent(freshIdentityToken, content);
        return await response;
      } catch (err) {
        return null;
      }
    }
    return null;
  };

  const getChatEndedReason = () => {
    const reasons = {
      PROVIDER: { type: ChatEndedReasonTypes.AGENT_DISCONNECTED },
      CLIENT: { type: ChatEndedReasonTypes.USER_DISCONNECTED },
      ERROR: { type: ChatEndedReasonTypes.CONNECTION_LOST },
    };
    return reasons.ERROR;
  };

  return {
    subscribeToAgentChatEvents: (handler: (event: AgentChatEvents) => void) => {
      agentEventHandler = handler;
      // Per the EnGen docs the provider will send an isTyping event when an agent starts typing
      // We can expect any subsequent isTyping events over the next 5 seconds if the agent keeps typing
      // The event should be automatically cleared after 5 seconds.
      let isTypingTimeout: ReturnType<typeof setTimeout>;
      const clearIsTyping = () => {
        handler({
          type: EventTypes.PARTICIPANT_TYPING,
          eventId: uuidv4(),
          isTyping: false,
        });
      };

      // STATUS CHANGE
      chatSDK.onStatusChange.subscribe((statusChange: StatusChange) => {
        chatInstance.setStatus(
          statusChange.chat.state,
          statusChange.connection,
        );
        if (
          (statusChange.chat.state === 'READY' ||
            statusChange.chat.state === 'ACTIVE') &&
          statusChange.connection === 'CONNECTED'
        ) {
          handler({ type: EventTypes.CHAT_STARTED, eventId: uuidv4() });
        }

        if (statusChange.chat.state === 'TRANSFERRING') {
          handler({
            eventId: uuidv4(),
            type: EventTypes.TRANSFER_REQUESTED,
          });
        }
        // `endedBy` is optional on the wire and is relayed verbatim from the
        // gateway, so it is absent whenever the SDK reports the end on its own
        // behalf — notably when the websocket closes before the gateway answers
        // a member-initiated `closeChat`. Keying off it meant those chats ended
        // silently and never triggered the post-chat survey. The terminal
        // COMPLETE state, not the reason recorded for it, marks a chat as over.
        if (statusChange.chat.state === ChatState.COMPLETE) {
          endChat(getChatEndedReason());
        }
      });

      // PARTICIPANT JOINED
      chatSDK.onParticipantUpdate.subscribe(
        (participantUpdate: ParticipantUpdate) => {
          const { action, participant } = participantUpdate;

          const participantInfo = {
            id: `${participant.id}`,
            name: participant.displayName,
          };

          switch (action) {
            case 'JOIN':
              handler({
                type: EventTypes.PARTICIPANT_JOINED,
                eventId: uuidv4(),
                participant: participantInfo,
              });
              break;
            case 'LEAVE':
              handler({
                type: EventTypes.PARTICIPANT_LEFT,
                eventId: uuidv4(),
                participant: participantInfo,
              });
              break;
            default:
              break;
          }
        },
      );

      chatSDK.onIsTyping.subscribe(() => {
        // If we've already set a timeout to trigger a PARTICIPANT_TYPING event with an isTyping value of false we need to reset the timer.
        if (isTypingTimeout) {
          clearTimeout(isTypingTimeout);
        }
        // We need to set a new timeout to trigger the PARTICIPANT_TYPING event with an isTyping value of false after 5 seconds.
        isTypingTimeout = setTimeout(clearIsTyping, 5000);
        handler({
          type: EventTypes.PARTICIPANT_TYPING,
          eventId: uuidv4(),
          isTyping: true,
        });
      });

      // MESSAGE RECEIVED
      chatSDK.onNewMessage.subscribe(
        (participantMessage: ParticipantMessage) => {
          const { messages, participant } = participantMessage;
          if (isTypingTimeout) {
            clearIsTyping();
          }
          // Generates the sender from the participant
          const sender = getMessageSender(participant);
          if (messages.length > 0) {
            // Messages come back as an array of individual messages
            // We need to iterate over them
            messages.forEach((message: MessageType) => {
              // MessageType TEXT
              if (message.messageType === 'TEXT') {
                // Detect if the message text contains a phone number that should render as a link.
                const messageTextHasPhoneNumber = hasPhoneNumber(message.text);

                handler({
                  type: EventTypes.MESSAGE,
                  eventId: uuidv4(),
                  // Messages with phone number will be handled for hyperlinking via the messageRichText property instead of the message property
                  message: messageTextHasPhoneNumber ? undefined : message.text,
                  messageRichText: messageTextHasPhoneNumber
                    ? linkifyPhoneNumbers(message.text)
                    : undefined,
                  sender,
                  timestamp: new Date(),
                });
                return;
              }
              // MessageType URL
              if (
                message.messageType === 'URL' ||
                message.messageType === 'FILE_LINK'
              ) {
                handler({
                  type: EventTypes.MESSAGE,
                  eventId: uuidv4(),
                  // Passing in the link value
                  // The FE will manage parsing it
                  message: message.link.value,
                  sender,
                  timestamp: new Date(),
                });
                return;
              }
              // MessageType SUGGESTIONS
              if (message.messageType === 'SUGGESTIONS') {
                const chips = getMessageChips(message.suggestions);
                handler({
                  type: EventTypes.MESSAGE,
                  eventId: uuidv4(),
                  chips,
                  sender,
                  timestamp: new Date(),
                });
                return;
              }
              // MessageType CARD
              // CARD is DEPRECATED. Maintaining this case because the type still exists in the EnGen sdk
              if (message.messageType === 'CARD') {
                const card = getMessageCard(message.card);
                handler({
                  type: EventTypes.MESSAGE,
                  eventId: uuidv4(),
                  cardList: [card],
                  sender,
                  timestamp: new Date(),
                });
              }
              // MessageType CAROUSEL
              if (message.messageType === 'CAROUSEL') {
                const cardList = getMessageCardList(message.carousel);
                handler({
                  type: EventTypes.MESSAGE,
                  eventId: uuidv4(),
                  cardList,
                  sender,
                  timestamp: new Date(),
                });
              }
            });
          }
        },
      );

      // ERROR RECEIVED
      chatSDK.onError.subscribe((error: ChatError) => {
        // If the chat errored out unexpectedly, we send the chat unavailable error.
        // This is a terminal state like any other, so it goes through `endChat`
        // to trigger the survey and to stay deduplicated against the COMPLETE
        // status change the gateway sends alongside an `endChat` error frame.
        if (error.code === 'ENDED_BY_ERROR') {
          endChat({ type: ChatEndedReasonTypes.CHAT_UNAVAILABLE });
          return undefined;
        }
        return handler({
          eventId: uuidv4(),
          type: EventTypes.ERROR_RECEIVED,
          errorType: {
            type: ErrorTypes.DEFAULT,
            ...(error?.message && { message: error.message }),
          },
        });
      });
      return null;
    },

    // User Chat Events
    handleUserChatEvents: async (event: UserChatEvents) => {
      let providerId: string | null | undefined;
      let chatStatus: ChatState;
      switch (event.type) {
        // Start Chat
        case EventTypes.START_CHAT:
          chatInstance.setConfig(event.config);
          // A new session gets its own chat end: re-arm the once-per-session
          // guard so a second chat can still surface the survey.
          chatEndHandled = false;
          providerId = await checkAvailability();
          chatStatus = chatInstance.getStatus();
          if (providerId && chatStatus === 'READY') {
            await startChat(providerId);
          } else {
            // If there was an error starting chat, we send the close chat event, which will trigger an error screen from enGen live chat
            const freshIdentityToken = await identityToken.getToken();
            if (freshIdentityToken) {
              // A chat that never started is not a completed interaction, so
              // this close is deliberately not routed through `endChat`.
              chatSDK.closeChat(freshIdentityToken).catch(() => undefined);
            }
          }
          break;

        case EventTypes.MESSAGE:
          if (event?.message) {
            const messageStatus = await sendMessage(event.message);
            // Ensures messageId is not null and that it can be converted to a string
            // Here it is returned as a number
            // In the onMessageAcknowledgement observable it is returned as a string.
            if (messageStatus?.id.toString()) {
              chatInstance.addUnacknowledgedMessage(
                messageStatus?.id.toString(),
                event.eventId,
              );
            }
          }
          if (event?.chips && event.chips.length > 0) {
            await Promise.all(
              event.chips.map(async (chip) => {
                const messageStatus = await sendChip(chip);
                // Ensures messageId is not null and that it can be converted to a string
                // In the onMessageAcknowledgement observable it is returned as a string.
                if (messageStatus?.id.toString()) {
                  chatInstance.addUnacknowledgedMessage(
                    messageStatus?.id.toString(),
                    event.eventId,
                  );
                }
              }),
            );
          }
          break;
        case EventTypes.PARTICIPANT_TYPING:
          if (event.isTyping) {
            chatSDK.sendIsTyping();
          }
          break;
        case EventTypes.END_CHAT: {
          try {
            const freshIdentityToken = await identityToken.getToken();
            if (freshIdentityToken) {
              await chatSDK.closeChat(freshIdentityToken);
            } else {
              // No token means closeChat can't run, so fall back to local endChat.
              captureAppMessage(
                'Live Chat - unable to end chat, no identity token',
                {
                  context: {
                    Context: {
                      info: 'END_CHAT was handled but no identity token was available, so closeChat could not be called; falling back to local endChat.',
                    },
                  },
                  ownershipTeam: SENTRY_CONSTANTS.OWNERSHIP_TEAM,
                  severityLevel: 'error',
                },
              );
              endChat({ type: ChatEndedReasonTypes.USER_DISCONNECTED });
            }
          } catch (closeChatError) {
            // getToken/closeChat failed with no status change, so fall back to local endChat.
            captureAppMessage('Live Chat - closeChat failed on END_CHAT', {
              context: {
                Context: {
                  info: 'closeChat rejected while ending a chat; falling back to a local CHAT_ENDED so the survey still shows.',
                  error:
                    closeChatError instanceof Error
                      ? closeChatError.message
                      : JSON.stringify(closeChatError),
                },
              },
              ownershipTeam: SENTRY_CONSTANTS.OWNERSHIP_TEAM,
              severityLevel: 'warning',
            });
            endChat({ type: ChatEndedReasonTypes.CONNECTION_LOST });
          }
          break;
        }
        default:
      }
    },
  };
};
